import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { db } from '../../config/firebase';

const clamp = (value) => Math.max(1, Math.min(5, Number(value || 0)));
const safeId = (value) => String(value || '').replace(/\//g, '_').trim();

const ratingDocId = (raterUserId, targetType, targetId) =>
  `${safeId(raterUserId)}__${safeId(targetType)}__${safeId(targetId)}`;

const categoryKeys = ['communication', 'professionalism', 'behaviour', 'reliability', 'businessValue'];

export async function getMyEntityRating({ raterUserId, targetType, targetId }) {
  if (!raterUserId || !targetType || !targetId) return null;
  const snap = await getDoc(doc(db, 'ratings', ratingDocId(raterUserId, targetType, targetId)));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function getEntityRatingSummary(targetType, targetId) {
  if (!targetType || !targetId) return { average: 0, count: 0 };
  const snap = await getDoc(doc(db, 'rating_summaries', `${safeId(targetType)}__${safeId(targetId)}`));
  return snap.exists() ? snap.data() : { average: 0, count: 0 };
}

export async function submitEntityRating({
  raterUserId,
  raterCompanyId,
  targetType,
  targetId,
  targetName,
  values,
  comment = '',
  contextType = 'business_interaction',
}) {
  if (!raterUserId) throw new Error('You must be signed in to rate.');
  if (!targetId || !['user', 'company'].includes(targetType)) throw new Error('Invalid rating target.');
  if (targetType === 'user' && String(targetId) === String(raterUserId)) throw new Error('You cannot rate your own profile.');
  if (targetType === 'company' && raterCompanyId && String(targetId) === String(raterCompanyId)) throw new Error('You cannot rate your own company.');

  const normalized = {};
  categoryKeys.forEach((key) => { normalized[key] = clamp(values?.[key] || values?.overall || 5); });
  const overall = clamp(values?.overall || (categoryKeys.reduce((sum, key) => sum + normalized[key], 0) / categoryKeys.length));
  const ref = doc(db, 'ratings', ratingDocId(raterUserId, targetType, targetId));
  const existing = await getDoc(ref);

  await setDoc(ref, {
    raterUserId,
    raterCompanyId: raterCompanyId || '',
    targetType,
    targetId,
    targetName: targetName || '',
    overall,
    ...normalized,
    contextType,
    comment: String(comment || '').trim().slice(0, 1200),
    createdAt: existing.exists() ? (existing.data()?.createdAt || serverTimestamp()) : serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });

  const all = await getDocs(query(
    collection(db, 'ratings'),
    where('targetType', '==', targetType),
    where('targetId', '==', targetId),
  ));
  const rows = all.docs.map((snap) => snap.data()).filter((row) => Number(row.overall) > 0);
  const count = rows.length;
  const avg = (key) => count ? rows.reduce((sum, row) => sum + Number(row[key] || row.overall || 0), 0) / count : 0;
  const summary = {
    average: Number(avg('overall').toFixed(2)),
    count,
    communication: Number(avg('communication').toFixed(2)),
    professionalism: Number(avg('professionalism').toFixed(2)),
    behaviour: Number(avg('behaviour').toFixed(2)),
    reliability: Number(avg('reliability').toFixed(2)),
    businessValue: Number(avg('businessValue').toFixed(2)),
    updatedAt: serverTimestamp(),
  };

  await setDoc(doc(db, 'rating_summaries', `${safeId(targetType)}__${safeId(targetId)}`), summary, { merge: true });
  return { ...summary, updatedAt: new Date() };
}

export default { getMyEntityRating, getEntityRatingSummary, submitEntityRating };
